/**
 * The end-of-line bench, one page: the search always on top, the expected
 * orders under it, and the order the operator chose. Nothing opens by itself.
 *
 * Once chosen, the only difference between two orders is whether they have
 * crates. A ready order of the flow is worked with its crates
 * ({@link OrdineCassette}); an order that never went through them (still on
 * the old system's printed sheet), found by number or by the sheet's barcode,
 * is opened by hand and shipped from here.
 *
 * The search is a read, not a gesture: it goes to the server straight, and its
 * answer lives only on this screen. Opening is a gesture (`fai`), and the order
 * shows up in the view's `manuali` once the server has taken it: the screen
 * moves to the order then, so a refused opening simply leaves the search where
 * it was, with the refusal in the notice.
 *
 * An order we already shipped opens read-only ({@link Banco} «Vedi»): no new
 * boxes once a carrier has the shipment — it is cancelled and redone instead.
 * A cancelled shipment is open again, and its order is worked as before.
 *
 * Without crates nothing says when the goods are all boxed: the boxes are
 * filled first, then «Pronte per la pesa» moves them to the weighing,
 * and the order leaves once every box is weighed.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { leggi, identitaCorrente } from '@errebi/sessione';
import {
  zRicercaManuale,
  type EsitoComando,
  type RicercaManuale,
  type RisultatoManuale,
} from '@errebi/shared';
import type { CarrierName, CarrierState, Consignee, ManualLine } from '@errebi/domain';

import { Spazio, Testata as Barra, Voce } from '../../components/header';
import { Icona } from '../../components/icon';
import { Modale } from '../../components/modal';
import { Sezione } from '../../components/section';
import { SpedizioneBrt } from './carrier-shipment';
import { OrdineCassette, type Cassette, type Lavoro, type Spedizione } from './crate-order';
import { GarmentPicture } from '../receiving/booth';
import { openCarrierLabels, openParcelBarcodes } from './label';

/** An order opened by hand, as `vistaFineLinea().manuali` sends it. */
export interface RigaManuale {
  readonly ordine: string;
  readonly cliente: string;
  readonly consegna: string;
  readonly ritiro: boolean;
  readonly colli: number;
  readonly pesati: number;
  readonly corriere: CarrierName | null;
  /** Closed, or held by the carrier; false again after a cancellation. */
  readonly partita: boolean;
  /** «Pronte per la pesa» pressed: in weighing, it leaves once every box is weighed. */
  readonly scatoleFinite: boolean;
  readonly fonteIndirizzo: 'spedizione' | 'fatturazione' | null;
  readonly destinatario: Consignee | null;
  /** The goods saved at opening; empty for an order opened before they were. */
  readonly righe: readonly ManualLine[];
}

/** The part of a shipment this screen reads. */
export interface SpedizioneManuale {
  readonly ordine: string;
  readonly colli: readonly {
    readonly barcode: string;
    readonly progressivo: number;
    readonly chiuso: boolean;
    readonly pesoKg: number | null;
    /** Our barcode label was printed; absent means not yet. */
    readonly barcodeStampato?: boolean;
  }[];
  readonly chiusa: boolean;
  readonly vettore?: CarrierState | null;
}

export interface PropsBanco {
  /** The ready orders of the flow (`daImballare`), worked with their crates. */
  readonly attesi: readonly Lavoro[];
  readonly manuali: readonly RigaManuale[];
  readonly spedizioni: readonly Spedizione[];
  readonly cassette: Cassette;
  readonly inCorso: boolean;
  readonly fai: (percorso: string, corpo?: unknown) => () => void;
  /** The outcome of the last gesture: after «Apri» it says whether it was taken. */
  readonly ultimo?: EsitoComando | null;
  /**
   * What goes at the bottom of the right column («Da sistemare», «Colli senza
   * peso», «Spedite»), told which order is on screen: what that order shows
   * already (its boxes, their weights, its shipment) is not listed twice.
   */
  readonly accanto?: (aSchermo: string | null) => ReactNode;
  /** What closes the header on the right (the notifications). */
  readonly inTestata?: ReactNode;
  /** Every order a box was opened for today, with where it stands. Optional for older views. */
  readonly oggi?: readonly OrdineDiOggi[];
}

/** A row of «Oggi», as `vistaFineLinea().oggi` sends it. */
export interface OrdineDiOggi {
  readonly ordine: string;
  readonly cliente: string;
  readonly colli: number;
  readonly stato: 'in-lavoro' | 'da-pesare' | 'etichette' | 'pronto' | 'da-sistemare';
  /** The carrier created the shipment: its labels can be printed from here. */
  readonly etichettePronte: boolean;
}

/**
 * The groups of «Oggi», top to bottom: what needs someone first, then the
 * steps of the round in their order. The three colours of the round are always
 * on screen, empty too: they read as the legend of the bench.
 */
const GRUPPI_DI_OGGI: readonly OrdineDiOggi['stato'][] = [
  'da-sistemare',
  'in-lavoro',
  'da-pesare',
  'etichette',
  'pronto',
];
const SEMPRE_A_SCHERMO: readonly OrdineDiOggi['stato'][] = ['da-pesare', 'etichette', 'pronto'];

/**
 * «Oggi»: the word and the colour of each state. Yellow to weigh, light blue
 * while the carrier labels wait to go on the boxes, green ready to leave.
 */
const STATO_DI_OGGI: Record<OrdineDiOggi['stato'], { parola: string; classe: string }> = {
  'in-lavoro': { parola: 'in lavoro', classe: 'riga--att' },
  'da-pesare': { parola: 'da pesare', classe: 'riga--da-pesare' },
  etichette: { parola: 'etichette da attaccare', classe: 'riga--etichette' },
  pronto: { parola: 'pronto per la spedizione', classe: 'riga--pronto' },
  'da-sistemare': { parola: 'da sistemare', classe: 'riga--da-sistemare' },
};

/**
 * The order on screen: its number and the customer to title it with. A ready
 * order of the flow also keeps the `Lavoro` it was chosen from: the last crate
 * poured takes it out of `attesi`, and it must stay on screen while its
 * shipment leaves.
 */
interface Scelto {
  readonly ordine: string;
  readonly cliente: string;
  readonly lavoro?: Lavoro;
}

const kg = (n: number): string => `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} kg`;

/** «Ordini attesi», for an order opened by hand: where it stands, in a few words. */
function rowState(r: RigaManuale): string {
  if (r.partita) return `${r.corriere ?? 'corriere'} partita`;
  if (r.ritiro) return 'ritiro in sede';
  if (r.colli === 0) return 'nessuna scatola';
  const scatole = r.colli === 1 ? '1 scatola' : `${r.colli} scatole`;
  if (!r.scatoleFinite) return `${scatole}, da riempire`;
  const daPesare = r.colli - r.pesati;
  return daPesare === 0 ? 'in pesa, tutte pesate' : `in pesa, ${daPesare} da pesare`;
}

function list(numeri: readonly number[]): string {
  return numeri.length === 1
    ? `della scatola ${numeri[0]}`
    : `delle scatole ${numeri.slice(0, -1).join(', ')} e ${numeri[numeri.length - 1]}`;
}

/** The status line of an open order: what is missing, and which carrier it leaves with. */
function statusLine(riga: RigaManuale | undefined, sped: SpedizioneManuale | undefined): string {
  const colli = sped?.colli ?? [];
  const corriere = sped?.vettore?.corriere ?? riga?.corriere ?? null;
  const ritiro = riga?.ritiro ?? false;
  if (sped?.vettore?.fase === 'da-sistemare') {
    return `Spedizione ${sped.vettore.corriere} da sistemare: la trovi in «Da sistemare».`;
  }
  if (sped?.vettore) return `Spedizione partita con ${sped.vettore.corriere}.`;
  if (riga?.partita && corriere) return `Spedizione partita con ${corriere}.`;
  if (sped?.chiusa) return ritiro ? 'Spedizione chiusa: ritiro in sede.' : 'Spedizione chiusa.';
  if (colli.length === 0) return 'Nessuna scatola: aggiungine una per ogni scatola che riempi.';
  const aperta = colli.find((c) => !c.chiuso);
  if (aperta) return `La scatola ${aperta.progressivo} è aperta: chiudila quando è pronta.`;
  if (!ritiro && riga !== undefined && !riga.scatoleFinite) {
    if (colli.some((c) => c.barcodeStampato !== true)) {
      return 'Stampa i barcode delle scatole: poi premi “Pronte per la pesa”.';
    }
    return 'Quando hai riempito tutte le scatole premi “Pronte per la pesa”: poi si passa alla pesa.';
  }
  const poi = ritiro
    ? 'Ritiro in sede: niente corriere.'
    : corriere
      ? `Poi la spedizione parte da sola con ${corriere}.`
      : 'Poi la spedizione parte da sola.';
  const senzaPeso = colli.filter((c) => c.pesoKg === null).map((c) => c.progressivo);
  if (senzaPeso.length > 0) return `Manca il peso ${list(senzaPeso)}. ${poi}`;
  if (ritiro) return 'Ritiro in sede: niente corriere.';
  return corriere ? `Tutte pesate: la spedizione parte con ${corriere}.` : 'Tutte pesate.';
}

const PROVENIENZA: Record<RisultatoManuale['provenienza'], string> = {
  deconetwork: 'da DecoNetwork',
  flusso: 'nel flusso',
  spedito: 'già spedito da noi',
};

export function Banco({
  attesi,
  manuali,
  spedizioni,
  cassette,
  inCorso,
  fai,
  ultimo,
  accanto,
  inTestata,
  oggi = [],
}: PropsBanco) {
  const [testo, setTesto] = useState('');
  // Why the carrier labels of an order in «Oggi» could not be opened.
  const [erroreStampa, setErroreStampa] = useState<string | null>(null);
  const [ricerca, setRicerca] = useState<{ testo: string; esito: RicercaManuale } | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [cercando, setCercando] = useState(false);
  const [scelto, setSceltoState] = useState<Scelto | null>(null);
  // The order that has made it to the screen: once it vanishes from every
  // list the choice is dropped, so it never comes back by itself.
  const [mostrato, setMostrato] = useState<string | null>(null);
  const setScelto = (s: Scelto | null) => {
    setSceltoState(s);
    setMostrato(null);
  };
  // The result waiting for «Apri lo stesso»: DecoNetwork says it already left.
  const [daConfermare, setDaConfermare] = useState<RisultatoManuale | null>(null);
  // The next scan must land in the search field, not on the button just pressed.
  const campo = useRef<HTMLInputElement>(null);
  const [aspettoApri, setAspettoApri] = useState(false);
  const [fuoco, setFuoco] = useState(0);
  // The outcome of «Apri» arrives as a new `ultimo`: read it while rendering,
  // the way React adjusts state to a changed prop.
  const [ultimoVisto, setUltimoVisto] = useState(ultimo);
  if (ultimo !== ultimoVisto) {
    setUltimoVisto(ultimo);
    if (aspettoApri && ultimo != null) {
      setAspettoApri(false);
      // A refused opening leaves the search: the order must not pop up later.
      // A taken one blanks it, ready for the next scan.
      if (!ultimo.ok) setScelto(null);
      else sbianca();
      setFuoco((n) => n + 1);
    }
  }

  useEffect(() => {
    campo.current?.focus();
  }, [fuoco, ricerca]);

  /** An order was opened from the results: the field starts blank next time. */
  function sbianca() {
    setTesto('');
    setRicerca(null);
    setErrore(null);
  }

  const cerca = async () => {
    const q = testo.trim();
    if (q === '') return;
    setCercando(true);
    setErrore(null);
    setScelto(null);
    try {
      const esito = await leggi(
        `/flusso/fine-linea/cerca?q=${encodeURIComponent(q)}`,
        identitaCorrente()?.token ?? null,
        zRicercaManuale,
      );
      setRicerca({ testo: q, esito });
    } catch (e) {
      setRicerca(null);
      setErrore(e instanceof Error ? e.message : 'La ricerca non ha risposto.');
    } finally {
      setCercando(false);
    }
  };

  const apri = (r: RisultatoManuale) => {
    setScelto({ ordine: r.ordine, cliente: r.cliente });
    setAspettoApri(true);
    fai('/fine-linea/apri', { ordine: r.ordine, idDeco: r.idDeco })();
  };

  const lavora = (l: Lavoro) => setScelto({ ordine: l.ordine, cliente: l.cliente, lavoro: l });
  // A ready order opens with its crates; anything else on what the view holds for it.
  const apriDiOggi = (o: OrdineDiOggi) => {
    const pronto = attesi.find((l) => l.ordine === o.ordine);
    if (pronto) lavora(pronto);
    else setScelto({ ordine: o.ordine, cliente: o.cliente });
  };
  const pronto = (ordine: string) => attesi.find((l) => l.ordine === ordine);
  // An order of the flow out of the ready list whose boxes are still being
  // filled: its last crate is poured, and only its shipment is left to finish.
  const inImballo = (ordine: string) =>
    spedizioni.some((s) => s.ordine === ordine && !s.chiusa && s.colli.length > 0);
  const lavoraRisultato = (r: RisultatoManuale) => {
    sbianca();
    lavora(
      pronto(r.ordine) ?? {
        ordine: r.ordine,
        cliente: r.cliente,
        consegna: r.consegna ?? '',
        destinatario: null,
        urgente: false,
        ritiro: r.ritiro,
        deroga: null,
        carrelli: [],
        capi: 0,
        cassette: [],
        misura: null,
      },
    );
  };

  // Hand-opened orders still being filled: once at the scale or gone they are
  // followed in «Oggi». A pick-up has no scale step and stays until closed.
  const daRiempire = manuali.filter((m) => !m.partita && (m.ritiro || !m.scatoleFinite));

  // The chosen order is on screen once the server knows it: ready with its
  // crates, opened by hand, or a shipment of ours to look at.
  const riga = scelto ? manuali.find((m) => m.ordine === scelto.ordine) : undefined;
  const sped = scelto ? spedizioni.find((s) => s.ordine === scelto.ordine) : undefined;
  // A ready order leaves `attesi` only once no crate is left out (or its
  // shipment is closed): kept on screen, it has nothing left to pour or to
  // fetch, and its shipment is what is left to see.
  const lavoro =
    (scelto && pronto(scelto.ordine)) ??
    (scelto?.lavoro && sped && !sped.chiusa
      ? { ...scelto.lavoro, carrelli: [], capi: 0, cassette: [], misura: null }
      : undefined);
  const conCassette = riga === undefined ? lavoro : undefined;
  const inVista =
    scelto !== null && (conCassette !== undefined || riga !== undefined || sped !== undefined);
  // Adjusted while rendering, like `ultimoVisto`. Not while «Apri» waits: the
  // order is chosen before the server has it.
  if (inVista && mostrato !== scelto.ordine) setMostrato(scelto.ordine);
  if (scelto !== null && !inVista && mostrato === scelto.ordine && !aspettoApri) setScelto(null);
  const torna = () => setScelto(null);

  return (
    <>
      <Barra>
        <Voce k="Fine linea" v={<>{attesi.length} ordini pronti</>} />
        <Spazio />
        {inVista && conCassette && conCassette.carrelli.length > 0 && (
          <Voce k="Vai a prendere" v={<>carrello {conCassette.carrelli.join(', ')}</>} />
        )}
        {inTestata}
      </Barra>

      <div className="main main--fine-linea">
        <section className="booth" style={{ background: 'var(--panel)' }}>
          {inVista && conCassette ? (
            <OrdineCassette
              lavoro={conCassette}
              sped={sped}
              cassette={cassette}
              inCorso={inCorso}
              fai={fai}
              azioni={<TornaAllaRicerca torna={torna} />}
            />
          ) : inVista ? (
            <Ordine
              scelto={scelto}
              riga={riga}
              sped={sped}
              inCorso={inCorso}
              fai={fai}
              torna={torna}
            />
          ) : (
            <>
              <Sezione titolo="Cerca un ordine">
                <form
                  className="mods"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void cerca();
                  }}
                >
                  <label className="campo" style={{ margin: 0, flex: 1 }}>
                    <input
                      ref={campo}
                      aria-label="Cerca un ordine"
                      autoFocus
                      value={testo}
                      onChange={(e) => setTesto(e.target.value)}
                    />
                  </label>
                  <button
                    type="submit"
                    className="btn btn--primario"
                    disabled={cercando || testo.trim() === ''}
                  >
                    <Icona nome="cerca" />
                    Cerca
                  </button>
                </form>
                <p className="mod__hint" style={{ margin: '8px 0 0' }}>
                  Numero d'ordine, oppure spara il barcode del foglio (es. .DHL3083315971E).
                </p>

                {errore && (
                  <p className="avviso avviso--no" style={{ margin: '12px 0 0' }}>
                    {errore}
                  </p>
                )}
                {ricerca && (
                  <Risultati
                    testo={ricerca.testo}
                    esito={ricerca.esito}
                    inCorso={inCorso}
                    apri={(r) => (r.avviso === null ? apri(r) : setDaConfermare(r))}
                    vedi={(r) => {
                      sbianca();
                      setScelto({ ordine: r.ordine, cliente: r.cliente });
                    }}
                    lavorabile={(r) => pronto(r.ordine) !== undefined || inImballo(r.ordine)}
                    lavora={lavoraRisultato}
                  />
                )}
              </Sezione>

              <Sezione
                titolo="Ordini attesi"
                distanziata
                accanto={<>{attesi.length + daRiempire.length}</>}
              >
                {attesi.length + daRiempire.length === 0 ? (
                  <p className="mod__hint">
                    Nessun ordine atteso: cercalo per numero o spara il barcode del foglio.
                  </p>
                ) : (
                  <ul
                    aria-label="Ordini attesi"
                    style={{ listStyle: 'none', padding: 0, margin: 0 }}
                  >
                    {/* The ready orders first, urgent on top as the server sorts them;
                        then the ones opened by hand today, with where they stand. */}
                    {attesi.map((l) => (
                      <li key={l.ordine}>
                        <button
                          type="button"
                          className={`riga riga--${l.urgente ? 'part' : 'att'}`}
                          onClick={() => lavora(l)}
                        >
                          <span className="riga__testo">
                            <span className="riga__cliente">{l.cliente}</span>
                            <span className="riga__sku">
                              ordine {l.ordine} · carrello {l.carrelli.join(', ')}
                            </span>
                            <span className="riga__conto">
                              {l.capi} capi · {l.cassette.length} cassette
                            </span>
                            {(l.urgente || l.ritiro) && (
                              <span className="riga__pers">
                                {[l.urgente && 'urgente', l.ritiro && 'ritiro in sede']
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                    {daRiempire.map((m) => (
                      <li key={m.ordine}>
                        <button
                          type="button"
                          className={`riga riga--${m.partita ? 'ok' : 'att'}`}
                          onClick={() => setScelto({ ordine: m.ordine, cliente: m.cliente })}
                        >
                          <span className="riga__cliente">
                            <b>{m.ordine}</b> · {m.cliente} · {rowState(m)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Sezione>
            </>
          )}
        </section>

        <section className="righe">
          {inVista && (
            <>
              {conCassette ? (
                <Testata
                  ordine={scelto.ordine}
                  cliente={conCassette.cliente}
                  consegna={conCassette.consegna}
                  ritiro={conCassette.ritiro}
                  destinatario={conCassette.destinatario ?? sped?.vettore?.destinatario ?? null}
                  fonte={null}
                />
              ) : (
                <Testata
                  ordine={scelto.ordine}
                  cliente={riga?.cliente ?? scelto.cliente}
                  consegna={riga?.consegna ?? scelto.lavoro?.consegna ?? ''}
                  ritiro={riga?.ritiro ?? scelto.lavoro?.ritiro ?? false}
                  destinatario={
                    riga?.destinatario ??
                    sped?.vettore?.destinatario ??
                    scelto.lavoro?.destinatario ??
                    null
                  }
                  fonte={riga?.fonteIndirizzo ?? null}
                />
              )}
              {riga && <Righe righe={riga.righe} />}
            </>
          )}
          <Sezione titolo="Oggi" accanto={<>{oggi.length}</>} distanziata={inVista}>
            {oggi.length === 0 && (
              <p className="mod__hint" style={{ margin: '0 0 8px' }}>
                Qui compaiono gli ordini su cui apri una scatola oggi, col colore del loro stato.
              </p>
            )}
            <ul aria-label="Oggi" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {GRUPPI_DI_OGGI.map((stato) => {
                const ordini = oggi.filter((o) => o.stato === stato);
                if (ordini.length === 0 && !SEMPRE_A_SCHERMO.includes(stato)) return null;
                return (
                  <li
                    key={stato}
                    role="group"
                    aria-label={STATO_DI_OGGI[stato].parola}
                    style={{ marginBottom: 12 }}
                  >
                    <div className={`oggi__titolo oggi__titolo--${stato}`}>
                      {STATO_DI_OGGI[stato].parola} · {ordini.length}
                    </div>
                    {ordini.map((o) => (
                      // The card carries the colour; the print button sits inside it, on the right.
                      <div
                        key={o.ordine}
                        className={`riga ${STATO_DI_OGGI[o.stato].classe}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 6,
                          marginLeft: 12,
                          // `.riga` is 100% wide: with the indent it would spill out of the column.
                          width: 'auto',
                        }}
                      >
                        <button type="button" className="oggi__apri" onClick={() => apriDiOggi(o)}>
                          <b>{o.ordine}</b> · {o.cliente} ·{' '}
                          {o.colli === 1 ? '1 collo' : `${o.colli} colli`}
                        </button>
                        {/* At the bench until the labelling machine after the scale does it. */}
                        {o.etichettePronte && (
                          <button
                            type="button"
                            // Once ready to leave it is only a reprint: an icon on the card's own background.
                            className={o.stato === 'pronto' ? 'btn btn--icona' : 'btn'}
                            aria-label={`Stampa etichette di ${o.ordine}`}
                            {...(o.stato === 'pronto' ? { title: 'Ristampa le etichette' } : {})}
                            onClick={() => {
                              setErroreStampa(null);
                              openCarrierLabels(o.ordine).catch((e: Error) =>
                                setErroreStampa(e.message),
                              );
                            }}
                          >
                            <Icona nome="stampa" sola={o.stato === 'pronto'} />
                            {o.stato !== 'pronto' && 'Stampa etichette'}
                          </button>
                        )}
                      </div>
                    ))}
                  </li>
                );
              })}
            </ul>
            {erroreStampa && (
              <p className="avviso avviso--no" style={{ margin: '8px 0 0' }}>
                {erroreStampa}
              </p>
            )}
          </Sezione>
          {accanto?.(inVista ? scelto.ordine : null)}
        </section>
      </div>

      {daConfermare && (
        <Modale
          etichetta={`Aprire l'ordine ${daConfermare.ordine}?`}
          ruolo="alertdialog"
          onChiudi={() => setDaConfermare(null)}
        >
          <p className="gesto__k">{daConfermare.avviso}</p>
          <div className="mods" style={{ marginTop: 12 }}>
            <button className="mod" autoFocus onClick={() => setDaConfermare(null)}>
              <Icona nome="indietro" />
              Torna indietro
            </button>
            <button
              className="mod mod--no"
              disabled={inCorso}
              onClick={() => {
                setDaConfermare(null);
                apri(daConfermare);
              }}
            >
              <Icona nome="apri" />
              Apri lo stesso
            </button>
          </div>
        </Modale>
      )}
    </>
  );
}

/** Back to the search field and the expected orders. */
function TornaAllaRicerca({ torna }: { readonly torna: () => void }) {
  return (
    <button type="button" className="mod" onClick={torna}>
      <Icona nome="indietro" />
      torna alla ricerca
    </button>
  );
}

interface PropsRisultati {
  readonly testo: string;
  readonly esito: RicercaManuale;
  readonly inCorso: boolean;
  readonly apri: (r: RisultatoManuale) => void;
  readonly vedi: (r: RisultatoManuale) => void;
  /** Whether an order of the flow is ready, or being boxed, to be worked with its crates. */
  readonly lavorabile: (r: RisultatoManuale) => boolean;
  readonly lavora: (r: RisultatoManuale) => void;
}

function Risultati({ testo, esito, inCorso, apri, vedi, lavorabile, lavora }: PropsRisultati) {
  const { risultati, avviso } = esito;
  return (
    <div style={{ marginTop: 14 }}>
      {avviso && (
        <p className="avviso avviso--no" style={{ margin: '0 0 12px' }}>
          {avviso}
        </p>
      )}
      {risultati.length === 0 && avviso === null && (
        <p className="avviso avviso--no" style={{ margin: 0 }}>
          Nessun ordine trovato per {testo}.
        </p>
      )}
      {risultati.map((r, i) => (
        <article
          key={`${r.ordine}-${r.idDeco ?? i}`}
          className={`riga riga--${r.provenienza === 'spedito' ? 'ok' : 'att'}`}
          // The gesture on the right, in the middle of the card: the eye reads the order, the hand finds the button.
          style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="riga__cliente">{r.cliente}</div>
            <div className="riga__sku">ordine {r.ordine}</div>
            <div className="riga__pers">
              <span>{PROVENIENZA[r.provenienza]}</span>
              {r.ritiro && <span> · ritiro in sede</span>}
            </div>
            <div className="riga__conto">
              {[r.consegna && `consegna ${r.consegna}`, `${r.righe} righe`, `${r.capi} capi`]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div className="mods" style={{ flex: '0 0 auto' }}>
            {r.provenienza === 'spedito' ? (
              <button className="btn" onClick={() => vedi(r)}>
                <Icona nome="vedi" />
                Vedi
              </button>
            ) : !r.apribile && lavorabile(r) ? (
              <button className="btn btn--primario" onClick={() => lavora(r)}>
                <Icona nome="apri" />
                Lavora
              </button>
            ) : !r.apribile ? (
              <p className="mod__hint" style={{ margin: 0 }}>
                Non è ancora pronto per l'imballo.
              </p>
            ) : (
              <button className="btn btn--primario" disabled={inCorso} onClick={() => apri(r)}>
                <Icona nome="apri" />
                Apri
              </button>
            )}
          </div>
        </article>
      ))}
      {risultati.some((r) => r.provenienza === 'deconetwork') && (
        <p className="mod__hint" style={{ margin: '8px 0 0' }}>
          Non è passato dalle cassette: lo apri a mano, l'indirizzo arriva da DecoNetwork.
        </p>
      )}
    </div>
  );
}

/** What goes in the boxes, as the old sheet prints it: code, colour, pieces per size. */
function Righe({ righe }: { readonly righe: readonly ManualLine[] }) {
  return (
    <Sezione titolo="Righe dell’ordine" accanto={<>{righe.reduce((n, r) => n + r.capi, 0)} capi</>}>
      {righe.length === 0 ? (
        <p className="mod__hint">Righe non disponibili: guarda il foglio.</p>
      ) : (
        <ul aria-label="Righe dell’ordine" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {righe.map((r, i) => (
            <li key={i} className="riga" style={{ marginTop: 8 }}>
              {/* Bigger than at receiving: here it tells which garment goes in the box. */}
              <span className="riga__miniatura" style={{ width: 72, height: 72 }}>
                <GarmentPicture
                  foto={null}
                  anteprima={r.anteprima}
                  colore={r.colore ?? ''}
                  genere={null}
                  alt={[r.articolo, r.colore].filter(Boolean).join(' · ')}
                  className="riga__mini"
                />
              </span>
              <span className="riga__testo">
                <span className="riga__cliente">
                  {r.articolo}
                  {r.colore && ` · ${r.colore}`}
                </span>
                {r.taglie.length > 0 && (
                  <span className="riga__sku">
                    {r.taglie.map((t) => `${t.taglia} ${t.capi}`).join(' · ')}
                  </span>
                )}
                <span className="riga__conto">{r.capi === 1 ? '1 capo' : `${r.capi} capi`}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Sezione>
  );
}

/**
 * The order's head: number, customer, due date, and who the parcels go to.
 * Where the address came from is known only for an order opened by hand.
 */
function Testata({
  ordine,
  cliente,
  consegna,
  ritiro,
  destinatario,
  fonte,
}: {
  readonly ordine: string;
  readonly cliente: string;
  readonly consegna: string;
  readonly ritiro: boolean;
  readonly destinatario: Consignee | null;
  readonly fonte: RigaManuale['fonteIndirizzo'];
}) {
  return (
    <Sezione titolo={`Ordine ${ordine}`}>
      <div className="riga__cliente">{cliente}</div>
      {consegna && <div className="mod__hint">consegna {consegna}</div>}
      <span className="gesto__k" style={{ display: 'block', marginTop: 10 }}>
        Destinatario
      </span>
      {ritiro ? (
        <p className="mod__hint">Ritiro in sede: niente corriere.</p>
      ) : destinatario === null ? (
        <p className="avviso avviso--no">Manca l'indirizzo.</p>
      ) : (
        <div className="riga" style={{ marginTop: 8 }}>
          <div className="riga__cliente">{destinatario.nome}</div>
          <div>{destinatario.indirizzo}</div>
          <div>
            {destinatario.cap} {destinatario.citta}
            {destinatario.provincia ? ` ${destinatario.provincia}` : ''} · {destinatario.nazione}
          </div>
          {destinatario.telefono && <div>tel. {destinatario.telefono}</div>}
          {fonte && <div className="mod__hint">da DecoNetwork · indirizzo di {fonte}</div>}
        </div>
      )}
    </Sezione>
  );
}

interface PropsOrdine {
  readonly scelto: Scelto;
  readonly riga: RigaManuale | undefined;
  readonly sped: SpedizioneManuale | undefined;
  readonly inCorso: boolean;
  readonly fai: (percorso: string, corpo?: unknown) => () => void;
  readonly torna: () => void;
}

function Ordine({ scelto, riga, sped, inCorso, fai, torna }: PropsOrdine) {
  const [errore, setErrore] = useState<string | null>(null);
  const { ordine } = scelto;
  const colli = sped?.colli ?? [];
  const aperta = colli.find((c) => !c.chiuso);
  // Once a carrier has the shipment (or a pick-up was closed) no box is added:
  // it is cancelled and redone, as in the automatic flow. `partita` is false
  // again after a cancellation: the order is worked as before.
  const soloLettura = riga === undefined || riga.partita || !!sped?.chiusa;
  const ritiro = riga?.ritiro ?? false;
  // The boxes are filled first; «Pronte per la pesa» moves them to the
  // weighing. A pick-up has no carrier step: its weights show as they come.
  const inPesa = !ritiro && riga?.scatoleFinite === true && !soloLettura;
  const mostraPeso = inPesa || ritiro || soloLettura;

  const stampa = () => {
    setErrore(null);
    openParcelBarcodes(ordine).catch((e: Error) => setErrore(e.message));
  };

  return (
    <Sezione
      titolo={
        <>
          {riga?.cliente ?? scelto.cliente} · ordine {ordine}
          {riga && ' · manuale'}
        </>
      }
      azioni={<TornaAllaRicerca torna={torna} />}
    >
      <span className="gesto__k">{inPesa ? 'Fase di pesa' : 'Scatole'}</span>
      {colli.map((c) => (
        <article key={c.barcode} className="riga riga--ok" style={{ marginTop: 8 }}>
          <div className="riga__cliente">
            Scatola {c.progressivo}
            {!c.chiuso && ' · aperta'}
          </div>
          <div className="riga__sku">{c.barcode}</div>
          <div className="mod__hint">
            {c.barcodeStampato === true ? 'barcode stampato' : 'barcode da stampare'}
          </div>
          {mostraPeso && (
            <div className="riga__conto">{c.pesoKg === null ? 'da pesare' : kg(c.pesoKg)}</div>
          )}
        </article>
      ))}

      <div className="mods mods--stampa mods--gesti" style={{ marginTop: 12 }}>
        {!soloLettura &&
          (aperta ? (
            <button
              className="btn btn--primario"
              disabled={inCorso}
              onClick={fai(`/fine-linea/${ordine}/chiudi-collo`)}
            >
              <Icona nome="scatola" />
              Chiudi scatola
            </button>
          ) : (
            <button
              className="btn btn--primario"
              disabled={inCorso}
              onClick={fai(`/fine-linea/${ordine}/collo`)}
            >
              <Icona nome="aggiungi" />
              Aggiungi scatola
            </button>
          ))}
        <button className="btn" disabled={colli.length === 0 || !!aperta} onClick={stampa}>
          <Icona nome="stampa" />
          Stampa barcode
        </button>
        {!soloLettura && !ritiro && !riga.scatoleFinite && (
          <button
            className="btn btn--pronta"
            disabled={
              inCorso ||
              colli.length === 0 ||
              !!aperta ||
              colli.some((c) => c.barcodeStampato !== true)
            }
            onClick={() => {
              // The boxes go to the scale on their own: the bench moves on to the next order.
              fai(`/fine-linea/${ordine}/scatole-finite`)();
              torna();
            }}
          >
            <Icona nome="pesa" />
            Pronte per la pesa
          </button>
        )}
        {!soloLettura && ritiro && (
          <button
            className="btn"
            disabled={inCorso || colli.length === 0 || !!aperta}
            onClick={fai(`/fine-linea/${ordine}/spedisci`)}
          >
            <Icona nome="fatto" />
            Chiudi spedizione
          </button>
        )}
      </div>
      {errore && (
        <p className="avviso avviso--no" style={{ margin: '8px 0 0' }}>
          {errore}
        </p>
      )}

      <p className="avviso" style={{ margin: '12px 0 0' }}>
        {statusLine(riga, sped)}
      </p>

      {sped?.vettore && sped.vettore.fase !== 'da-sistemare' && (
        <div style={{ marginTop: 12 }}>
          <SpedizioneBrt
            ordine={ordine}
            colli={colli.length}
            vettore={sped.vettore}
            inCorso={inCorso}
            fai={fai}
          />
        </div>
      )}
    </Sezione>
  );
}
